import { dt } from '@common/util/dateTime';
import { TickUnit } from '@common/valueSeries';
import { AxisBottom } from '@visx/axis';
import { AxisProps } from '@visx/axis/lib/axis/Axis';
import { Group } from '@visx/group';
import { memo, useMemo } from 'react';
import { chartTheme, ScaleTickRange } from './chartHelper';

const zoomTypes = [
    {
        daysOver: 1200,
        format: 'YYYY',
        uniquenessFormat: 'YYYY',
        formatFirstDayAsMonth: false,
        formatFirstMonthAsMonthYear: false,
    },
    {
        daysOver: 365,
        format: 'MMM',
        uniquenessFormat: 'MM/YYYY',
        formatFirstDayAsMonth: false,
        formatFirstMonthAsMonthYear: true,
    },
    {
        daysOver: 0,
        format: 'D',
        uniquenessFormat: 'DD/MM/YYYY',
        formatFirstDayAsMonth: true,
        formatFirstMonthAsMonthYear: false,
    },
];

// We hide first label when it is too close to second to avoid overlap
const FIRST_GAP_ACCEPTABLE_PCT = 2.8;

// Caps amount of visible ticks in chart, hiding labels in between to prevent overlap.
// TODO: Consider exposing as prop for context-specific behavior.
const VISIBLE_TICKS_CONSTRAINT = 13;

export default memo(function DatesXAxis({
    scale,
    ...props
}: AxisProps<ScaleTickRange> & { unit: TickUnit }) {
    const zoomType = useMemo(() => {
        const xDomain = scale.domain();
        const daysInRange = dt(xDomain.at(-1)).diff(xDomain.at(0), 'days') + 1;
        return zoomTypes.find((z) => daysInRange >= z.daysOver) || zoomTypes[0];
    }, [scale]);

    const xAxisTickValues = useMemo(() => {
        const xDomain = scale.domain();
        const ticks: string[] = [];
        let previousValue = '';
        let itemsInFirstTick = 0;

        xDomain.forEach((dateStr, index) => {
            const dateDt = dt(dateStr);
            const value = dateDt.format(zoomType.uniquenessFormat);
            if (value !== previousValue) {
                previousValue = value;
                if (ticks.length === 1) itemsInFirstTick = index;

                ticks.push(dateStr);
            }
        });

        // If the first tick's month index is odd, and we're formatting the first month as YYYY,
        // remove the first tick. This ensures that we *always* show the year when formatting
        // the first date of each year
        if (
            zoomType.formatFirstMonthAsMonthYear &&
            dt(ticks[0]).month() % 2 === 1
        ) {
            ticks.shift();
        }

        const itemsInFirstTickPct = (itemsInFirstTick / xDomain.length) * 100;
        if (itemsInFirstTickPct < FIRST_GAP_ACCEPTABLE_PCT) {
            ticks[0] = '';
        }

        const keepTickEveryN = Math.floor(
            ticks.length / VISIBLE_TICKS_CONSTRAINT
        );
        if (keepTickEveryN > 0) {
            ticks.forEach((tick, index) => {
                if (index % keepTickEveryN !== 0) ticks[index] = '';
            });
        }

        return ticks;
    }, [
        scale,
        zoomType.uniquenessFormat,
        zoomType.formatFirstMonthAsMonthYear,
    ]);

    const shownMonths = new Set<number>();

    const format = (date: string) => {
        if (!date) return;

        const dateDt = dt(date);
        const month = dateDt.month();
        if (zoomType.formatFirstDayAsMonth && !shownMonths.has(month)) {
            shownMonths.add(month);
            return dateDt.format('MMM');
        }

        if (zoomType.formatFirstMonthAsMonthYear && dateDt.month() === 0)
            return dateDt.format('YYYY');

        return dateDt.format(zoomType.format);
    };

    return (
        <Group {...props}>
            <AxisBottom
                top={6}
                scale={scale}
                hideAxisLine={true}
                hideTicks={true}
                hideZero={true}
                tickValues={xAxisTickValues}
                tickFormat={(date) => format(date)}
                tickLabelProps={() => ({
                    fontSize: chartTheme.fontSize,
                    fill: chartTheme.colors.label,
                    textAnchor: 'middle',
                })}
            />
        </Group>
    );
});
